import g from '#core/global';
import load from '#core/load';


const gate_$ = '[data-gate]';
const view_tracker_$ = '[data-gated-view-tracker]';
const main_$ = 'main';
const gated_sidebar_$ = '.sidebar';
const exempt_pattern_$ = '.modal';
const download_link_$ = '.download-anchor-group .anchor__link';
const preview_interactive_$ = 'a[href], button, input, select, textarea, summary, [role="button"], [data-jump-selector], .expandable-button';
const jump_selector_attr = 'data-jump-selector';
const gate_boundary_attr = 'data-gate-boundary';
const gate_active_attr = 'data-gate-active';
const gate_hidden_sidebar_attr = 'data-gate-sidebars-hidden';
const aria_disabled_attr = 'aria-disabled';
const overlay_height_var = '--gate-overlay-height';
const gate_disabled_attr = 'data-gate-disabled';
const gate_prev_tabindex_attr = 'data-gate-prev-tabindex';
const gate_prev_aria_attr = 'data-gate-prev-aria-disabled';
const gate_prev_disabled_attr = 'data-gate-prev-disabled';

let $gate = null;
let $gated_patterns = [];
let $sequence_root = null;
let $preview_root = null;
let resize_observer = null;
let $hidden_sidebars = [];
let unlock_cleanup = null;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('gated-content')) return false;

		load.content(() => {
			const $view_tracker = document.querySelector(view_tracker_$);
			if ($view_tracker) record_returning_view($view_tracker);

			$gate = document.querySelector(gate_$);
			if (!$gate) return false;

			if ($gate.dataset.gatePreview === g.true_s || $gate.dataset.gatePreview === 'true') {
				return false;
			}

			setup();
		});
	},

};

export default module;
module.init();


/**
 * Set up gate behavior
 */
function setup() {
	$sequence_root = get_sequence_root();
	if (!$sequence_root) return false;
	$preview_root = get_preview_root();
	if (!$preview_root) return false;

	$preview_root.setAttribute(gate_boundary_attr, '');

	let $sibling = $preview_root.nextElementSibling;
	while ($sibling) {
		const $next = $sibling.nextElementSibling;
		if (!$sibling.matches(exempt_pattern_$)) {
			$gated_patterns.push({
				$el: $sibling,
				$parent: $sibling.parentNode,
				$next,
			});
			$sibling.remove();
		}
		$sibling = $next;
	}

	const $main = document.querySelector(main_$);
	$main.appendChild($gate);
	if ($sequence_root !== $gate) {
		$sequence_root.remove();
	}
	$gate.removeAttribute('hidden');

	hide_connected_sidebars();
	disable_download_links();
	disable_jump_links();
	disable_preview_interactions();
	bind_unlock_handlers();
	track_overlay_height();
	observe_boundary();
}


/**
 * Expose overlay height as a CSS variable on the boundary so it can size
 * itself relative to the overlay (boundary cap = 30dvh + overlay height).
 */
function track_overlay_height() {
	const update = () => {
		$preview_root.style.setProperty(overlay_height_var, `${$gate.offsetHeight}px`);
	};
	update();
	resize_observer = new ResizeObserver(update);
	resize_observer.observe($gate);
}


/**
 * Disable all download anchor links
 */
function disable_download_links() {
	if ($gate.dataset.gateHideSidebars !== 'true') return;

	const $downloads = document.querySelectorAll(download_link_$);
	$downloads.forEach($link => {
		$link.setAttribute(aria_disabled_attr, g.true_s);
	});
}


/**
 * Disable all jump nav links while the gate is present
 */
function disable_jump_links() {
	if ($gate.dataset.gateHideSidebars !== 'true') return;

	const $jump_links = document.querySelectorAll(`[${jump_selector_attr}]`);
	$jump_links.forEach($link => {
		$link.setAttribute(aria_disabled_attr, g.true_s);
	});
}


/**
 * Observe boundary pattern for scroll trigger
 */
function observe_boundary() {
	const activate_observer = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			if (entry.isIntersecting) activate_gate();
		});
	}, {
		rootMargin: '0px 0px -10% 0px',
	});
	activate_observer.observe($preview_root);

	const hide_observer = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			if (!entry.isIntersecting && entry.boundingClientRect.top > 0) {
				hide_gate();
			}
		});
	}, {
		rootMargin: '0px 0px 30% 0px',
	});
	hide_observer.observe($preview_root);
}


/**
 * Activate the gate overlay (slide up)
 */
function activate_gate() {
	if ($gate.hasAttribute(gate_active_attr)) return;
	$gate.setAttribute(gate_active_attr, '');
	$gate.setAttribute('tabindex', '-1');
	$gate.focus({ preventScroll: true });
}


/**
 * Hide the gate overlay (slide down) without restoring content
 */
function hide_gate() {
	$gate.removeAttribute(gate_active_attr);
}


/**
 * Deactivate the gate and restore content
 */
function deactivate_gate() {
	$gate.removeAttribute(gate_active_attr);

	if (typeof unlock_cleanup === 'function') {
		unlock_cleanup();
		unlock_cleanup = null;
	}

	$gated_patterns.forEach(({
		$el,
		$parent,
		$next,
	}) => {
		if (!$parent) return;

		if ($next?.parentNode === $parent) {
			$parent.insertBefore($el, $next);
			return;
		}

		$parent.appendChild($el);
	});
	$gated_patterns = [];

	$preview_root.removeAttribute(gate_boundary_attr);
	$preview_root.style.removeProperty(overlay_height_var);
	restore_connected_sidebars();
	restore_preview_interactions();

	if (resize_observer) {
		resize_observer.disconnect();
		resize_observer = null;
	}

	document.querySelectorAll(`[${aria_disabled_attr}="${g.true_s}"]`)
		.forEach($el => $el.removeAttribute(aria_disabled_attr));

	$gate.addEventListener(g.transition_end_e, () => $gate.remove(), { once: true });
}


function reload_unlocked_page() {
	window.location.reload();
}


function bind_unlock_handlers() {
	let is_unlocked = false;
	let confirmation_observer = null;
	let jquery_bound = false;
	const form_id = parseInt($gate.dataset.gateFormId || '', 10);

	const unlock = () => {
		if (is_unlocked) return;
		is_unlocked = true;
		deactivate_gate();
		window.setTimeout(reload_unlocked_page, 50);
	};

	const maybe_unlock_from_confirmation = () => {
		if ($gate.querySelector('.gform_confirmation_wrapper, .gform_confirmation_message')) {
			unlock();
		}
	};

	if (window.jQuery) {
		window.jQuery(document).on('gform_confirmation_loaded.gatedContent', (event, confirmed_form_id) => {
			if (!Number.isNaN(form_id) && confirmed_form_id !== form_id) return;
			unlock();
		});
		jquery_bound = true;
	}

	confirmation_observer = new MutationObserver(maybe_unlock_from_confirmation);
	confirmation_observer.observe($gate, {
		childList: true,
		subtree: true,
	});

	$gate.addEventListener('gated-content:unlock', unlock, { once: true });
	maybe_unlock_from_confirmation();

	unlock_cleanup = () => {
		if (confirmation_observer) {
			confirmation_observer.disconnect();
			confirmation_observer = null;
		}

		if (jquery_bound && window.jQuery) {
			window.jQuery(document).off('gform_confirmation_loaded.gatedContent');
		}
	};
}

function record_returning_view($tracker) {
	const endpoint = $tracker.dataset.gatedViewUrl;
	const resource_id = parseInt($tracker.dataset.gatedResourceId || '', 10);
	if (!endpoint || Number.isNaN(resource_id)) return;

	const view_id = window.crypto?.randomUUID
		? window.crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;

	fetch(endpoint, {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			resource_id,
			view_id,
		}),
		keepalive: true,
	}).catch(() => {
		// View tracking must never interrupt access to the resource.
	});
}


function hide_connected_sidebars() {
	if ($gate.dataset.gateHideSidebars !== 'true') return;

	$hidden_sidebars = Array.from($preview_root.querySelectorAll(gated_sidebar_$));
	$hidden_sidebars.forEach($sidebar => {
		$sidebar.setAttribute(gate_hidden_sidebar_attr, '');
		$sidebar.inert = true;
		$sidebar.hidden = true;
	});
}


function restore_connected_sidebars() {
	if (!$hidden_sidebars.length) return;

	$hidden_sidebars.forEach($sidebar => {
		$sidebar.inert = false;
		$sidebar.hidden = false;
		$sidebar.removeAttribute(gate_hidden_sidebar_attr);
	});

	$hidden_sidebars = [];
}


function disable_preview_interactions() {
	const $interactives = $preview_root.querySelectorAll(preview_interactive_$);

	$interactives.forEach($el => {
		if ($el.hasAttribute(gate_disabled_attr)) return;

		$el.setAttribute(gate_disabled_attr, '');

		if ($el.hasAttribute('tabindex')) {
			$el.setAttribute(gate_prev_tabindex_attr, $el.getAttribute('tabindex'));
		}

		if ($el.hasAttribute(aria_disabled_attr)) {
			$el.setAttribute(gate_prev_aria_attr, $el.getAttribute(aria_disabled_attr));
		}

		$el.setAttribute('tabindex', '-1');
		$el.setAttribute(aria_disabled_attr, g.true_s);

		if ('disabled' in $el) {
			$el.setAttribute(gate_prev_disabled_attr, $el.disabled ? g.true_s : 'false');
			$el.disabled = true;
		}
	});
}


function restore_preview_interactions() {
	const $interactives = $preview_root.querySelectorAll(`[${gate_disabled_attr}]`);

	$interactives.forEach($el => {
		if ($el.hasAttribute(gate_prev_tabindex_attr)) {
			$el.setAttribute('tabindex', $el.getAttribute(gate_prev_tabindex_attr));
			$el.removeAttribute(gate_prev_tabindex_attr);
		} else {
			$el.removeAttribute('tabindex');
		}

		if ($el.hasAttribute(gate_prev_aria_attr)) {
			$el.setAttribute(aria_disabled_attr, $el.getAttribute(gate_prev_aria_attr));
			$el.removeAttribute(gate_prev_aria_attr);
		} else {
			$el.removeAttribute(aria_disabled_attr);
		}

		if ('disabled' in $el && $el.hasAttribute(gate_prev_disabled_attr)) {
			$el.disabled = $el.getAttribute(gate_prev_disabled_attr) === g.true_s;
			$el.removeAttribute(gate_prev_disabled_attr);
		}

		$el.removeAttribute(gate_disabled_attr);
	});
}


function get_sequence_root() {
	const $main = document.querySelector(main_$);
	if (!$main) return null;

	let $node = $gate;
	while ($node?.parentElement && $node.parentElement !== $main) {
		$node = $node.parentElement;
	}

	return $node;
}


function get_preview_root() {
	let $node = $sequence_root.nextElementSibling;

	while ($node && $node.matches(exempt_pattern_$)) {
		$node = $node.nextElementSibling;
	}

	return $node;
}
